import React from "react";

import { PreclassCheckSettings } from "components/local_modal/preclass_check/settings";
import { ForumAPI } from "contexts/forum_api_context";
import { AppDispatch } from "pubsub/client";
import {
  CpuModelAndScoreType,
  ExtensionCpuInfoType,
  ExtensionMemoryInfoType,
  GeekBenchScoreResultType,
  HardwareMemoryInfoType,
  HardwareStatsType,
} from "shared/types/hardware_monitor";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { createMonitoringEvent } from "store/reducers/monitoring";

declare global {
  interface Window {
    MinervaSystemCheckExtension: {
      getCpuInfo: () => {
        on: (event: string, callback: (result: { data: ExtensionCpuInfoType }) => void) => void;
      };
      getMemoryInfo: () => {
        on: (event: string, callback: (result: { data: ExtensionMemoryInfoType }) => void) => void;
      };
    };
  }
}

class HardwareMonitor {
  cpuUsage: number;

  requestTimeout?: NodeJS.Timeout;

  hardwareLoadTimeout?: NodeJS.Timeout;

  shouldTriggerHardwareLoadEvent: boolean;

  extensionMonitoringInterval?: NodeJS.Timeout;

  forumAPI: ForumAPI;

  previousCpuInfo?: ExtensionCpuInfoType;

  cpuModelAndScore?: CpuModelAndScoreType;

  userId?: string;

  constructor(dispatch?: AppDispatch, userId?: string) {
    this.cpuUsage = 0;
    this.shouldTriggerHardwareLoadEvent = true;
    this.forumAPI = new ForumAPI(dispatch, userId);
    this.userId = userId;
  }

  private maybeRecordLoadInClassStart() {
    this.hardwareLoadTimeout = setTimeout(() => {
      const promise = this.calculateExtensionCpuInInterval();
      if (promise) {
        promise.then((cpuUsage) => {
          if (cpuUsage && this.userId) {
            createMonitoringEvent({
              type: MonitoringEventTypeType.HARDWARE_LOAD,
              userId: this.userId,
              contextData: { hardwareInfo: cpuUsage },
            });
          }
        });
      }
    }, PreclassCheckSettings.CLASS_LOAD_CPU_LOAD_MONITOR_TIME);
  }

  maybeStartMonitoringHardware() {
    window.flightLogger.log(`[HardwareMonitor] Starting hardware monitoring.`);
    this.maybeRecordLoadInClassStart();
    this.extensionMonitoringInterval = setInterval(
      this.recordHardwareLoadInExtension.bind(this),
      PreclassCheckSettings.EXTENSION_CPU_LOAD_MONITOR_INTERVAL,
    );
  }

  private recordHardwareLoadInExtension() {
    this.calculateExtensionCpuInInterval().then((cpuUsage) => {
      if (cpuUsage) {
        // Create Monitoring event
        createMonitoringEvent({
          type: MonitoringEventTypeType.EXTENSION_STATS,
          userId: "userId",
          contextData: { hardwareInfo: cpuUsage },
        });
      }
    });
  }

  private calculateExtensionCpuInInterval() {
    if (!HardwareMonitor.checkCpuExtension()) {
      return Promise.resolve();
    }
    const hardwareStats: HardwareStatsType = {
      topData: [],
      totalUsage: {
        id: "total",
        idle: 0,
        kernel: 0,
        user: 0,
      },
      totalLoad: 0,
      totalMemory: 0,
      memory: {},
      geekbenchMultiScore: 0,
    };
    return HardwareMonitor.getExtensionCpuInfoType().then((result) => {
      const cpuInfo = result as ExtensionCpuInfoType;
      if (!cpuInfo) {
        return;
      }
      for (let i = 0; i < cpuInfo.numOfProcessors; i += 1) {
        const usage = cpuInfo.processors[i].usage;
        const processorUsage = {
          id: i,
          kernel: 0,
          user: 0,
          idle: 0,
        };
        if (this.previousCpuInfo) {
          const oldUsage = this.previousCpuInfo.processors[i].usage;
          processorUsage.kernel = Math.floor(
            ((usage.kernel - oldUsage.kernel) / (usage.total - oldUsage.total)) * 100,
          );
          processorUsage.user = Math.floor(
            ((usage.user - oldUsage.user) / (usage.total - oldUsage.total)) * 100,
          );
          processorUsage.idle = Math.floor(
            ((usage.idle - oldUsage.idle) / (usage.total - oldUsage.total)) * 100,
          );
        } else {
          processorUsage.kernel = Math.floor((usage.kernel / usage.total) * 100);
          processorUsage.user = Math.floor((usage.user / usage.total) * 100);
          processorUsage.idle = Math.floor((usage.idle / usage.total) * 100);
        }
        hardwareStats.topData[i] = processorUsage;
      }
      const totalUsage = {
        id: "total",
        idle: 0,
        kernel: 0,
        user: 0,
      };
      hardwareStats.topData.forEach((cpuInProcessor) => {
        totalUsage.idle += cpuInProcessor.idle;
        totalUsage.kernel += cpuInProcessor.kernel;
        totalUsage.user += cpuInProcessor.user;
      });
      totalUsage.idle /= cpuInfo.numOfProcessors;
      totalUsage.kernel /= cpuInfo.numOfProcessors;
      totalUsage.user /= cpuInfo.numOfProcessors;
      hardwareStats.totalUsage = totalUsage;
      hardwareStats.totalLoad = totalUsage.user + totalUsage.kernel;
      this.previousCpuInfo = cpuInfo;
      return HardwareMonitor.getMemoryInfo().then((memoryResult) => {
        if (!memoryResult) {
          return hardwareStats;
        }
        hardwareStats.totalMemory = memoryResult.totalInGB;
        hardwareStats.memory = memoryResult;
        hardwareStats.geekbenchMultiScore = this.getGeekbenchMultiScore();
        return hardwareStats;
      });
    });
  }

  private static getCpuModelName(): Promise<{ source: string; modelName: string }> {
    return new Promise((resolve) => {
      const modelNameResult = {
        source: "none",
        modelName: "",
      };
      if (HardwareMonitor.checkCpuExtension()) {
        HardwareMonitor.getExtensionCpuInfoType().then((cpuInfo) => {
          if (!cpuInfo) {
            resolve(modelNameResult);
            return;
          }
          modelNameResult.source = "extension";
          if (cpuInfo.modelName) {
            modelNameResult.modelName = cpuInfo.modelName;
            resolve(modelNameResult);
            return;
          }
          resolve(modelNameResult);
        });
      } else {
        resolve(modelNameResult);
      }
    });
  }

  private async getGeekbenchScoreForModel(
    modelName: string,
    platform: string,
  ): Promise<GeekBenchScoreResultType> {
    const response = await this.forumAPI.get(
      `api/v1/systemcheck/get-benchmark-score?cpu=${modelName}&platform=${platform}`,
    );
    return response as unknown as GeekBenchScoreResultType;
  }

  getGeekbenchMultiScore() {
    if (
      this.cpuModelAndScore &&
      this.cpuModelAndScore.geekbench &&
      this.cpuModelAndScore.geekbench.found &&
      this.cpuModelAndScore.geekbench["multi-score"]
    ) {
      return this.cpuModelAndScore.geekbench["multi-score"];
    }
    return false;
  }

  gatherCpuInfo() {
    return new Promise((resolve) => {
      if (this.cpuModelAndScore) {
        resolve(this.cpuModelAndScore);
      }
      HardwareMonitor.getCpuModelName().then((modelNameResult) => {
        const cpuModelAndScore = { ...modelNameResult } as CpuModelAndScoreType;
        const modelName = cpuModelAndScore.modelName;
        if (modelName) {
          const platform = navigator.userAgent.indexOf("Mac OS X") !== -1 ? "mac" : "default";
          this.getGeekbenchScoreForModel(modelName, platform).then((result) => {
            cpuModelAndScore.geekbench = result;
            this.cpuModelAndScore = { ...cpuModelAndScore };
            resolve(cpuModelAndScore);
          });
        } else {
          this.cpuModelAndScore = { ...cpuModelAndScore };
          resolve(cpuModelAndScore);
        }
      });
    });
  }

  private static getExtensionCpuInfoType(): Promise<ExtensionCpuInfoType | null> {
    return new Promise((resolve) => {
      if (!HardwareMonitor.checkCpuExtension()) {
        resolve(null);
        return;
      }
      window.MinervaSystemCheckExtension.getCpuInfo().on("success", (result) => {
        resolve(result.data);
      });
    });
  }

  static getMemoryInfo(): Promise<HardwareMemoryInfoType | null> {
    return new Promise((resolve) => {
      if (HardwareMonitor.checkMemoryExtension()) {
        window.MinervaSystemCheckExtension.getMemoryInfo().on("success", (result) => {
          if (result.data) {
            const memoryInfo = {
              info: result.data,
              totalInGB: result.data.capacity / 1024 / 1024 / 1024,
            };
            resolve(memoryInfo);
          } else {
            resolve(null);
          }
        });
        return;
      }
      resolve(null);
    });
  }

  private static checkCpuExtension() {
    return window.MinervaSystemCheckExtension && window.MinervaSystemCheckExtension.getCpuInfo;
  }

  private static checkMemoryExtension() {
    return window.MinervaSystemCheckExtension && window.MinervaSystemCheckExtension.getMemoryInfo;
  }

  stopMonitoringHardware() {
    if (this.hardwareLoadTimeout) {
      clearTimeout(this.hardwareLoadTimeout);
    }
    if (this.extensionMonitoringInterval) {
      clearInterval(this.extensionMonitoringInterval);
    }
  }
}

const HardwareMonitorContext = React.createContext<HardwareMonitor>(new HardwareMonitor());

export { HardwareMonitor, HardwareMonitorContext };
